import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ContentAsset,
  ContentAssetLinkRequest,
  ContentDetail,
  ContentSummary,
  HomeContent,
  PageResponse,
  SuccessStorySubmission,
  UpcomingEvent,
  UpcomingEventFilters,
  UpcomingEventListFilters,
  UpcomingEventRegistration,
  UpcomingEventRegistrationRequest,
  UpcomingEventStatistics,
} from '../models';

/** Contenu public du site (accueil, actualités, success stories…), porté par content-service. */
@Injectable({ providedIn: 'root' })
export class ContentService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/content-service/api/v1/contents/public`;
  private readonly meUrl = `${environment.apiUrl}/content-service/api/v1/contents/me`;

  /** Blocs publics de la page d'accueil : contenus mis en avant et derniers publiés (UC-01). */
  home(): Observable<HomeContent> {
    return this.http.get<HomeContent>(`${this.baseUrl}/home`);
  }

  /**
   * Success stories publiées. Contrairement à opportunity-service et media-library-service,
   * ce endpoint ne prend aucun paramètre de recherche texte (son contrôleur ne déclare pas de
   * `q`) : il retourne toujours tout le contenu publié. Le filtrage par mot-clé (UC-03) se fait
   * donc côté client sur ce jeu de données, voir SearchService.
   */
  successStories(size = 50): Observable<PageResponse<ContentSummary>> {
    const params = new HttpParams().set('size', size);
    return this.http.get<PageResponse<ContentSummary>>(`${this.baseUrl}/success-stories`, { params });
  }

  /** Détail d'une success story par slug (UC-22). */
  successStory(slug: string): Observable<ContentDetail> {
    return this.http.get<ContentDetail>(`${this.baseUrl}/success-stories/${slug}`);
  }

  /** Actualités publiées (content-service, /contents/public/news) : bloc "Actualités" de l'accueil et page dédiée. */
  news(size = 20): Observable<PageResponse<ContentSummary>> {
    const params = new HttpParams().set('page', 0).set('size', size).set('sort', 'publishedAt,desc');
    return this.http.get<PageResponse<ContentSummary>>(`${this.baseUrl}/news`, { params });
  }

  /** Détail d'une actualité par slug. */
  newsArticle(slug: string): Observable<ContentDetail> {
    return this.http.get<ContentDetail>(`${this.baseUrl}/news/${slug}`);
  }

  /** Consultation publique (POST /contents/public/{id}/view) : alimente analytics-service, non bloquant. */
  trackView(contentId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/${contentId}/view`, {});
  }

  /** Événements mis en avant (bloc Agenda de l'accueil, UC-01). */
  featuredUpcomingEvents(size = 3): Observable<PageResponse<UpcomingEvent>> {
    const params = new HttpParams().set('page', 0).set('size', size);
    return this.http.get<PageResponse<UpcomingEvent>>(`${this.baseUrl}/upcoming-events/featured`, { params });
  }

  /**
   * Repli quand aucun événement n'est mis en avant : les 90 prochains jours, déjà triés par
   * date de début côté backend. Volontairement PAS `GET /upcoming-events` (liste/recherche) —
   * cet endpoint renvoie une 500 (`lower(bytea)`) dès que `query` n'est pas fourni, côté
   * content-service (confirmé en prod le 2026-08-07) ; `/calendar` n'a pas ce paramètre et
   * évite le bug.
   */
  upcomingEventsCalendar(): Observable<UpcomingEvent[]> {
    return this.http.get<UpcomingEvent[]>(`${this.baseUrl}/upcoming-events/calendar`);
  }

  /**
   * Page publique "Événements à venir" (WF-EVT-001). `query` est TOUJOURS envoyé, y compris vide
   * (`''`) : c'est ce qui évite la 500 `lower(bytea)` du contrôleur (voir upcomingEventsCalendar
   * ci-dessus) — Postgres ne peut résoudre le type du paramètre `lower(:query)` que lorsqu'une
   * vraie chaîne (même vide) est liée, jamais quand le paramètre est absent/null.
   */
  upcomingEvents(filters: UpcomingEventListFilters = {}): Observable<PageResponse<UpcomingEvent>> {
    let params = new HttpParams()
      .set('page', filters.page ?? 0)
      .set('size', filters.size ?? 12)
      .set('query', filters.query?.trim() ?? '');
    if (filters.regionCode) params = params.set('regionCode', filters.regionCode);
    if (filters.cityCode) params = params.set('cityCode', filters.cityCode);
    if (filters.eventType) params = params.set('eventType', filters.eventType);
    if (filters.categoryCode) params = params.set('categoryCode', filters.categoryCode);
    if (filters.targetAudience) params = params.set('targetAudience', filters.targetAudience);
    return this.http.get<PageResponse<UpcomingEvent>>(`${this.baseUrl}/upcoming-events`, { params });
  }

  /** Régions, villes, types et catégories réellement disponibles — à charger avant/pendant la page liste. */
  upcomingEventFilters(): Observable<UpcomingEventFilters> {
    return this.http.get<UpcomingEventFilters>(`${this.baseUrl}/upcoming-events/filters`);
  }

  /** Compteurs publics (publiés/brouillons/annulés/archivés). */
  upcomingEventStatistics(): Observable<UpcomingEventStatistics> {
    return this.http.get<UpcomingEventStatistics>(`${this.baseUrl}/upcoming-events/statistics`);
  }

  /** Détail public par identifiant. */
  upcomingEvent(eventId: string): Observable<UpcomingEvent> {
    return this.http.get<UpcomingEvent>(`${this.baseUrl}/upcoming-events/${eventId}`);
  }

  /** Détail public par slug (route SEO, WF-EVT-002). */
  upcomingEventBySlug(slug: string): Observable<UpcomingEvent> {
    return this.http.get<UpcomingEvent>(`${this.baseUrl}/upcoming-events/slug/${slug}`);
  }

  /** Consultation d'un événement (POST .../{id}/view) : alimente analytics-service, non bloquant. */
  trackEventView(eventId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/upcoming-events/${eventId}/view`, {});
  }

  /** Partage d'un événement (POST .../{id}/share) : alimente analytics-service, non bloquant. */
  trackEventShare(eventId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/upcoming-events/${eventId}/share`, {});
  }

  /** UC-01/WF-EVT-003 : mes inscriptions aux événements à venir. */
  myEventRegistrations(size = 50): Observable<PageResponse<UpcomingEventRegistration>> {
    const params = new HttpParams().set('page', 0).set('size', size);
    return this.http.get<PageResponse<UpcomingEventRegistration>>(`${this.meUrl}/upcoming-events/registrations`, { params });
  }

  /** S'inscrit à un événement ; le backend bascule en WAITLISTED si la capacité est atteinte. */
  registerForEvent(eventId: string, payload: UpcomingEventRegistrationRequest): Observable<UpcomingEventRegistration> {
    return this.http.post<UpcomingEventRegistration>(`${this.meUrl}/upcoming-events/${eventId}/registrations`, payload);
  }

  /** Annule ma propre inscription à un événement. */
  cancelEventRegistration(eventId: string): Observable<unknown> {
    return this.http.delete(`${this.meUrl}/upcoming-events/${eventId}/registrations/me`);
  }

  /** UC-23 : mes témoignages proposés, avec leur statut de modération. */
  mySuccessStories(size = 20): Observable<PageResponse<ContentDetail>> {
    const params = new HttpParams().set('size', size);
    return this.http.get<PageResponse<ContentDetail>>(`${this.meUrl}/success-stories`, { params });
  }

  /**
   * Détail d'un de mes témoignages par id. À n'appeler qu'avec un id obtenu via
   * mySuccessStories() : le backend ne vérifie pas la propriété sur cet endpoint (IDOR connu,
   * voir mémo backend-issues-log), donc ne jamais accepter un id venant de l'URL sans contrôle.
   */
  mySuccessStory(id: string): Observable<ContentDetail> {
    return this.http.get<ContentDetail>(`${this.meUrl}/success-stories/${id}`);
  }

  /** Propose un nouveau témoignage : créé en statut SUBMITTED, jamais DRAFT (pas de brouillon serveur). */
  submitSuccessStory(request: SuccessStorySubmission): Observable<ContentDetail> {
    return this.http.post<ContentDetail>(`${this.meUrl}/success-stories`, request);
  }

  /** Corrige un témoignage (ex. suite à une demande de correction) sans changer son statut. */
  updateSuccessStory(id: string, request: SuccessStorySubmission): Observable<ContentDetail> {
    return this.http.patch<ContentDetail>(`${this.meUrl}/success-stories/${id}`, request);
  }

  /** Repasse un témoignage corrigé en SUBMITTED pour re-modération. */
  resubmitSuccessStory(id: string): Observable<ContentDetail> {
    return this.http.post<ContentDetail>(`${this.meUrl}/success-stories/${id}/submit`, {});
  }

  /** Retire un témoignage (archivage logique côté backend). */
  deleteSuccessStory(id: string): Observable<unknown> {
    return this.http.delete(`${this.meUrl}/success-stories/${id}`);
  }

  /** Rattache une vidéo (déjà envoyée dans media-library-service) comme vidéo principale de mon témoignage. */
  setSuccessStoryVideo(id: string, mediaId: string): Observable<ContentDetail> {
    return this.http.post<ContentDetail>(`${this.meUrl}/success-stories/${id}/video-media/${mediaId}`, {});
  }

  /** Retire le lien vidéo (le fichier reste dans media-library-service). */
  removeSuccessStoryVideo(id: string): Observable<ContentDetail> {
    return this.http.delete<ContentDetail>(`${this.meUrl}/success-stories/${id}/video-media`);
  }

  /** Galerie ordonnée (images/vidéos/audio/document) de mon témoignage. */
  successStoryMedia(id: string): Observable<ContentAsset[]> {
    return this.http.get<ContentAsset[]>(`${this.meUrl}/success-stories/${id}/media`);
  }

  /** Ajoute un média déjà envoyé dans media-library-service à la galerie de mon témoignage. */
  addSuccessStoryMedia(id: string, request: ContentAssetLinkRequest): Observable<ContentAsset> {
    return this.http.post<ContentAsset>(`${this.meUrl}/success-stories/${id}/media`, request);
  }

  /** Retire un média de la galerie (le fichier source n'est pas supprimé). */
  removeSuccessStoryMedia(id: string, assetId: string): Observable<unknown> {
    return this.http.delete(`${this.meUrl}/success-stories/${id}/media/${assetId}`);
  }
}
